/**
 * Edit Flow Utility Functions
 * Centralized utilities for managing edit flows across quote journey
 */

// Constants for session storage keys
export const EDIT_FLOW_STORAGE_KEYS = {
  QUOTE_REVIEW_EDIT_SECTION: "quoteReviewEditSection",
} as const;

// Edit section types
export type EditSectionType =
  | "cover"
  | "destination"
  | "organiser"
  | "extras"
  | string
  | null;

// Edit flow types
export enum EditFlowType {
  QUOTE_REVIEW_COVER_EDIT = "QUOTE_REVIEW_COVER_EDIT", // From quote-review page, editing cover details
  QUOTE_PAGE_EDIT = "QUOTE_PAGE_EDIT", // From quote page, standard edit flow
  NORMAL_FLOW = "NORMAL_FLOW", // Normal journey, not editing
}

/**
 * Safely checks if window is available (for SSR compatibility)
 */
export const isWindowAvailable = (): boolean => {
  return typeof window !== "undefined";
};

/**
 * Gets the edit section from session storage for quote review summary flow
 */
export const getQuoteReviewSummaryEditSection = (): EditSectionType => {
  if (!isWindowAvailable()) return null;

  const section = sessionStorage.getItem(
    EDIT_FLOW_STORAGE_KEYS.QUOTE_REVIEW_EDIT_SECTION
  );
  return section as EditSectionType;
};

/**
 * Sets the edit section in session storage for quote review summary flow
 */
export const setQuoteReviewSummaryEditSection = (
  section: EditSectionType
): void => {
  if (!isWindowAvailable()) return;

  if (section === null) {
    sessionStorage.removeItem(EDIT_FLOW_STORAGE_KEYS.QUOTE_REVIEW_EDIT_SECTION);
  } else {
    sessionStorage.setItem(
      EDIT_FLOW_STORAGE_KEYS.QUOTE_REVIEW_EDIT_SECTION,
      section
    );
  }
};

/**
 * Clears all edit flow session storage
 */
export const clearEditFlowSession = (): void => {
  if (!isWindowAvailable()) return;

  sessionStorage.removeItem(EDIT_FLOW_STORAGE_KEYS.QUOTE_REVIEW_EDIT_SECTION);
};

/**
 * Determines the current edit flow type based on URL params and session storage
 *
 * @param editPageParam - Edit page URL parameter (e.g., from searchParams.get('coverEdit'))
 * @param expectedSection - Expected section type (e.g., 'cover', 'traveller')
 * @returns EditFlowType enum value
 */
export const getEditFlowType = (
  editPageParam: string | null,
  expectedSection: EditSectionType = "cover"
): EditFlowType => {
  // Not in edit mode
  if (!editPageParam) {
    return EditFlowType.NORMAL_FLOW;
  }

  // In edit mode - check if from quote-review
  const editSection = getQuoteReviewSummaryEditSection();

  if (editSection === expectedSection) {
    return EditFlowType.QUOTE_REVIEW_COVER_EDIT;
  }

  return EditFlowType.QUOTE_PAGE_EDIT;
};

/**
 * Checks if current flow is quote-review cover edit
 * Convenience function for components that need simple boolean check
 */
export const isQuoteReviewCoverEdit = (
  editPageParam: string | null
): boolean => {
  return (
    getEditFlowType(editPageParam, "cover") ===
    EditFlowType.QUOTE_REVIEW_COVER_EDIT
  );
};

/**
 * Checks if component is the last step in quote-review cover edit flow
 * Used to determine if "Finish Editing" button should be shown
 */
export const isLastStepInCoverEdit = (
  editPageParam: string | null
): boolean => {
  return isQuoteReviewCoverEdit(editPageParam);
};
